/**
 * DS-internal hook : le système demande-t-il moins d'animation ?
 *
 * `prefers-reduced-motion: reduce` sur le web, « Réduire les animations » sur iOS et Android. Un composant animé lit
 * ce drapeau pour passer ses durées à zéro : le livre veut « le résultat arrive », pas le mouvement pour lui-même.
 *
 * Au web, la requête média est écoutée directement : `AccessibilityInfo` de react-native-web range ses écouteurs sous
 * le texte de la fonction reçue, le même pour toutes les instances, si bien qu'un composant qui se démonte retirait
 * l'écouteur d'un autre — qui ne voyait plus la préférence changer.
 */
import { useEffect, useState } from "react"
import { AccessibilityInfo, Platform } from "react-native"

const QUERY = "(prefers-reduced-motion: reduce)"

function webMedia(): MediaQueryList | null {
    if (Platform.OS !== "web" || typeof window === "undefined" || typeof window.matchMedia !== "function") return null
    return window.matchMedia(QUERY)
}

export function useReduceMotion(): boolean {
    const [reduceMotion, setReduceMotion] = useState(() => webMedia()?.matches ?? false)

    useEffect(() => {
        const media = webMedia()
        if (media) {
            const onChange = (event: MediaQueryListEvent) => setReduceMotion(event.matches)
            setReduceMotion(media.matches)
            media.addEventListener("change", onChange)
            return () => media.removeEventListener("change", onChange)
        }
        let alive = true
        AccessibilityInfo.isReduceMotionEnabled().then((on) => {
            if (alive) setReduceMotion(on)
        })
        const sub = AccessibilityInfo.addEventListener("reduceMotionChanged", setReduceMotion)
        return () => {
            alive = false
            sub.remove()
        }
    }, [])

    return reduceMotion
}
